import { useState } from "react";
import { Phone, Mail } from "lucide-react";

import { Button } from "@/components/ui/button";
import { EditClubInfoSheet } from "./edit-club-info-sheet";
import { authClient } from "@/lib/auth-client";
import { Skeleton } from "@/components/ui/skeleton";

export interface ClubInfo {
  name: string;
  phone: string;
  email: string;
  logo: string | null;
}

export function ClubInfoCard() {
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  // Use active organization from better-auth
  const { data: activeOrg, isPending } = authClient.useActiveOrganization();

  const handleUpdateSuccess = () => {
    // The useActiveOrganization hook will automatically refresh
  };

  if (isPending) {
    return <Skeleton className="h-64 w-64 rounded-lg" />;
  }

  if (!activeOrg) {
    return (
      <div className="flex h-64 w-64 items-center justify-center rounded-lg border bg-white p-4">
        <span className="text-sm text-gray-500">Aucune organisation</span>
      </div>
    );
  }

  // Map organization data to ClubInfo format
  const clubInfo: ClubInfo = {
    name: activeOrg.name,
    phone: activeOrg.phoneNumber || "",
    email: activeOrg.clubEmail || "",
    logo: activeOrg.logo || null,
  };

  // Generate initials for fallback avatar
  const initials = clubInfo.name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <div>
      <div className="flex flex-col items-center rounded-lg border bg-white p-5">
        {/* Logo */}
        <div className="mb-3 flex h-20 w-20 items-center justify-center overflow-hidden rounded-full border-4 border-orange-100 bg-orange-50">
          {clubInfo.logo ? (
            <img src={clubInfo.logo} alt={clubInfo.name} className="h-full w-full object-cover" />
          ) : (
            <span className="text-2xl font-bold text-orange-600">{initials}</span>
          )}
        </div>

        {/* Club name */}
        <h3 className="mb-4 text-xl font-bold text-gray-900">{clubInfo.name}</h3>

        {/* Contact info */}
        <div className="mb-4 space-y-2 text-center">
          {clubInfo.phone && (
            <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
              <Phone className="h-4 w-4 text-gray-400" />
              <span>{clubInfo.phone}</span>
            </div>
          )}
          {clubInfo.email && (
            <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
              <Mail className="h-4 w-4 shrink-0 text-gray-400" />
              <span className="max-w-[180px] truncate">{clubInfo.email}</span>
            </div>
          )}
        </div>

        {/* Edit button */}
        <Button variant="outline" onClick={() => setIsSheetOpen(true)}>
          Modifier
        </Button>
      </div>

      {/* Edit sheet */}
      <EditClubInfoSheet
        open={isSheetOpen}
        onOpenChange={setIsSheetOpen}
        clubInfo={clubInfo}
        onSuccess={handleUpdateSuccess}
      />
    </div>
  );
}
